import {Divider} from '@/components/ui/divider/Divider'
import {PageCardWrapper, PageContent} from '@/components/ui/page-atoms/PageAtoms'
import {ScrollArea} from '@/components/ui/scroll-area/ScrollArea'
import {Scrollbar} from '@/components/ui/scrollbar/Scrollbar'
import {ProjectFormHeading} from '@/features/projects/components/project-form/project-form-heading/ProjectFormHeading'
import {EarlyClosingProjectFormProvider} from '@/features/projects/components/project-form/early-closing-project-form-provider/EarlyClosingProjectFormProvider'
import {EarlyClosingProjectFormValidationSchema} from '@/features/projects/formSchemas'
import {ProjectExtended} from '@/features/projects/types'
import {useTranslation} from '@/translations/i18n'
import {Helmet} from 'react-helmet'
import {useOutletContext} from 'react-router-dom'
import {EarlyClosingProjectFormSections} from '@/features/projects/components/project-form/early-closing-project-form-sections/EarlyClosingProjectFormSections'
import {useFormContext, useWatch} from 'react-hook-form'
import {z} from 'zod'
import {VariationProjectConfirmationModal} from '@/features/projects/components/variation-project-confirmation-modal/VariationProjectConfirmationModal'
import {EarlyClosingProjectConfirmationModal} from '@/features/projects/components/early-closing-project-confirmation-modal/EarlyClosingProjectConfirmationModal'
import {usePermissions} from '@/features/auth/store/store'
import {NoPermissionBox} from '@/components/commons/no-permission-box/NoPermissionBox'
import {canEarlyCloseProject} from '@/features/projects/utils'
import {ProjectOperationNotAllowedBox} from '@/features/projects/components/project-operation-not-allowed-box/ProjectOperationNotAllowedBox'
import {useDialogStore} from '@ariakit/react'
import {useState} from 'react'

export const ProjectEarlyClosing = () => {
    const {t} = useTranslation()
    const permissions = usePermissions()
    const {project} = useOutletContext<{project: ProjectExtended}>()

    if (!permissions?.projects.write) {
        return <NoPermissionBox />
    }

    // Guard against direct URL access: only open projects not already closed early are eligible.
    if (!canEarlyCloseProject(project)) {
        return <ProjectOperationNotAllowedBox operation="earlyClosing" projectId={project.id} />
    }

    return (
        <PageCardWrapper>
            <Helmet title={t('projects:renew_meta_title')} />
            <EarlyClosingProjectFormProvider project={project}>
                <ProjectFormHeadingWrapper project={project} />
                <Divider direction="horizontal" />
                <PageContent padding={0}>
                    <ScrollArea style={{height: '100%'}} scrollbar={<Scrollbar />}>
                        <EarlyClosingProjectFormSections />
                    </ScrollArea>
                </PageContent>
            </EarlyClosingProjectFormProvider>
        </PageCardWrapper>
    )
}

export const ProjectFormHeadingWrapper = ({project}: {project: ProjectExtended}) => {
    const {t} = useTranslation()
    const {control, getValues} = useFormContext<EarlyClosingProjectFormValidationSchema>()
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)
    const confirmationModalStore = useDialogStore({open: isConfirmationModalOpen, setOpen: setIsConfirmationModalOpen})
    const hasReopening = useWatch({control, name: 'hasReopening'})

    const schema = hasReopening
        ? EarlyClosingProjectFormValidationSchema
        : z.object({
              id: z.string(),
              endDate: z.date()
          })

    return (
        <>
            <ProjectFormHeading
                title={t('projects:project_early_closing_title')}
                schema={schema}
                showConfirmationModal={() => confirmationModalStore.setOpen(true)}
                buttonLabel={t('projects:close_project_early')}
            />
            {isConfirmationModalOpen &&
                (hasReopening ? (
                    <VariationProjectConfirmationModal
                        project={project}
                        dialogStore={confirmationModalStore}
                        defaultValues={getValues()}
                    />
                ) : (
                    <EarlyClosingProjectConfirmationModal
                        dialogStore={confirmationModalStore}
                        defaultValues={getValues()}
                        project={project}
                    />
                ))}
        </>
    )
}
